/**
 * Implementação dos 6 perfis de comportamento tático da Inteligência Artificial.
 *
 * Perfis:
 * - agressivo: persegue o inimigo e ataca sempre que possível.
 * - defensivo: prioriza terrenos vantajosos e evita avançar desprotegido.
 * - guardiao: só reage se o inimigo invadir seu raio de alcance.
 * - cacador: persegue ativamente comandantes, curandeiros e unidades frágeis.
 * - patrulha: desloca-se por waypoints até avistar inimigos.
 * - chefe: mantém sua posição estratégica (forte/trono) e ataca sem se mover.
 */
import { pathTo, travelField } from '../movement.js';
import { weaponOf } from '../state.js';
import { curableBy, hasEffect } from '../states.js';
import { distance, positionKey, spanDistance } from '../types.js';
import { combatantOf, comparePositions, scoreAttack, scoreDefensivePosition, scoreHealing, } from './evaluator.js';
const NO_THREAT = { damageAt: () => 0, attackersAt: () => 0 };
/**
 * O quanto a unidade leva a sério o perigo da casa onde vai parar.
 *
 * Zero na fácil de propósito: uma IA que nunca se expõe é impossível de
 * flanquear, e a dificuldade fácil existe para perdoar. Na difícil o peso
 * passa de 1 — parar sob fogo cruzado custa mais do que o dano nominal,
 * porque tira a unidade da próxima jogada.
 */
const THREAT_WEIGHT = { facil: 0.15, normal: 0.7, dificil: 1.25 };
export function evaluateActionsForProfile(params) {
    const { state, content, unit, range, spots, enemies, allies, options } = params;
    const difficulty = options.difficulty ?? 'normal';
    const profile = unit.aiProfile;
    const weapon = weaponOf(content, unit);
    const threat = params.threat ?? NO_THREAT;
    const threatWeight = THREAT_WEIGHT[difficulty];
    /**
     * O desconto de parar numa casa perigosa.
     *
     * Três parcelas, porque são três coisas diferentes: o dano esperado, o
     * agravante de estar cercado (dois inimigos batendo é pior do que a soma
     * dos dois, porque não há como revidar aos dois) e o veto quase absoluto de
     * parar numa casa onde o dano previsto já mata.
     */
    const exposure = (at) => {
        const incoming = threat.damageAt(at);
        if (incoming <= 0)
            return 0;
        const attackers = threat.attackersAt(at);
        let penalty = incoming * threatWeight;
        if (attackers > 1)
            penalty += incoming * 0.2 * (attackers - 1) * threatWeight;
        // Casa onde a unidade morre. Só a fácil ainda considera ir.
        if (incoming >= unit.currentHp)
            penalty += difficulty === 'facil' ? 15 : 120;
        return penalty;
    };
    // --- Chefe: só ataca da posição atual, nunca abandona sua base/trono ----
    if (profile === 'chefe') {
        if (weapon && !weapon.heals) {
            let bestAttack = null;
            const attacker = combatantOf(state, content, unit, unit.position);
            for (const enemy of enemies) {
                const d = spanDistance(unit.position, unit.size, enemy.position, enemy.size);
                if (d >= weapon.range[0] && d <= weapon.range[1]) {
                    const defender = combatantOf(state, content, enemy, enemy.position);
                    const evalRes = scoreAttack(content, attacker, unit, defender, enemy, d, difficulty);
                    if (evalRes && (bestAttack === null || evalRes.total > bestAttack.score)) {
                        bestAttack = { score: evalRes.total, targetId: enemy.id };
                    }
                }
            }
            if (bestAttack) {
                return {
                    type: 'atacar',
                    unitId: unit.id,
                    path: [],
                    targetId: bestAttack.targetId,
                };
            }
        }
        // Chefe permanece imóvel guardando o trono
        return { type: 'mover', unitId: unit.id, path: [] };
    }
    // --- Suporte / Curandeiro: socorre aliados se portar cajado -------------
    // Silenciado, o cajado não responde: a tentativa seria recusada pelo motor.
    if (weapon?.heals && !hasEffect(unit.states, 'sem_magia')) {
        // Ferido, ou com condição que este cajado tira.
        const cures = (ally) => curableBy(ally.states, weapon.cures).length;
        const woundedAllies = allies
            .filter((a) => a.id !== unit.id && (a.currentHp < a.stats.hp || cures(a) > 0))
            .sort((a, b) => a.currentHp / a.stats.hp - b.currentHp / b.stats.hp || a.id - b.id);
        let bestHeal = null;
        const healPower = weapon.might + Math.floor(unit.stats.mag / 2);
        for (const ally of woundedAllies) {
            for (const from of spots) {
                const d = spanDistance(from, unit.size, ally.position, ally.size);
                if (d >= weapon.range[0] && d <= weapon.range[1]) {
                    const score = scoreHealing(ally, healPower, difficulty, cures(ally));
                    if (bestHeal === null ||
                        score > bestHeal.score ||
                        (score === bestHeal.score && comparePositions(from, bestHeal.from) < 0)) {
                        bestHeal = { score, from, targetId: ally.id };
                    }
                }
            }
        }
        if (bestHeal && bestHeal.score > 0) {
            return {
                type: 'curar',
                unitId: unit.id,
                path: pathTo(range, bestHeal.from) ?? [],
                targetId: bestHeal.targetId,
            };
        }
    }
    // --- Avaliação de Ataque para perfis ofensivos/reativos ------------------
    let bestAttack = null;
    if (weapon && !weapon.heals) {
        for (const enemy of enemies) {
            // Perfil Caçador dá prioridade a comandantes e alvos frágeis
            const hunterMultiplier = profile === 'cacador' && (enemy.isCommander || enemy.currentHp < enemy.stats.hp * 0.5)
                ? 1.5
                : 1.0;
            for (const from of spots) {
                const d = spanDistance(from, unit.size, enemy.position, enemy.size);
                if (d < weapon.range[0] || d > weapon.range[1])
                    continue;
                const attacker = combatantOf(state, content, unit, from);
                const defender = combatantOf(state, content, enemy, enemy.position);
                const evalRes = scoreAttack(content, attacker, unit, defender, enemy, d, difficulty);
                if (!evalRes)
                    continue;
                let totalScore = evalRes.total * hunterMultiplier;
                // Perfil defensivo valoriza terreno onde vai ficar para atacar
                if (profile === 'defensivo') {
                    totalScore += scoreDefensivePosition(content, state, from, d, difficulty);
                }
                /*
                 * E aqui entra o que a conta não via: o resto do exército inimigo.
                 *
                 * A avaliação media o duelo com o alvo — o dano dado e o contra-ataque
                 * dele — e ignorava todo mundo que também alcança aquela casa. Era isso
                 * que produzia o avanço suicida: dar cinco de dano num goblin parando
                 * ao alcance de outros três, e o placar dizer que valeu a pena.
                 *
                 * Matar o alvo alivia a conta, porque um inimigo a menos é um inimigo
                 * que não revida — mas só o dele, não o dos outros.
                 */
                totalScore -= exposure(from) * (evalRes.isLethal ? 0.6 : 1);
                if (bestAttack === null ||
                    totalScore > bestAttack.score.total ||
                    (totalScore === bestAttack.score.total &&
                        (enemy.id < bestAttack.targetId ||
                            (enemy.id === bestAttack.targetId && comparePositions(from, bestAttack.from) < 0)))) {
                    bestAttack = { score: { ...evalRes, total: totalScore }, from, targetId: enemy.id };
                }
            }
        }
    }
    // Se encontrou um ataque viável
    if (bestAttack && bestAttack.score.total > 0) {
        return {
            type: 'atacar',
            unitId: unit.id,
            path: pathTo(range, bestAttack.from) ?? [],
            targetId: bestAttack.targetId,
        };
    }
    // --- Comportamento quando nenhum ataque imediato está ao alcance ---------
    // Guardião: NÃO se move se nenhum inimigo entrou em seu alcance de ataque
    if (profile === 'guardiao') {
        return { type: 'mover', unitId: unit.id, path: [] };
    }
    // Defensivo: se ninguém está ao alcance, procura o melhor terreno defensivo próximo ou fica parado
    if (profile === 'defensivo') {
        let bestDefSpot = unit.position;
        let bestDefScore = scoreDefensivePosition(content, state, unit.position, enemies[0] ? spanDistance(unit.position, unit.size, enemies[0].position, enemies[0].size) : 99, difficulty) - exposure(unit.position);
        for (const spot of spots) {
            const closestDist = enemies.reduce((min, e) => Math.min(min, spanDistance(spot, unit.size, e.position, e.size)), 999);
            // Não corre sozinho em direção à morte se for defensivo
            const defScore = scoreDefensivePosition(content, state, spot, closestDist, difficulty) - exposure(spot);
            if (defScore > bestDefScore || (defScore === bestDefScore && comparePositions(spot, bestDefSpot) < 0)) {
                bestDefScore = defScore;
                bestDefSpot = spot;
            }
        }
        if (positionKey(bestDefSpot) !== positionKey(unit.position)) {
            return { type: 'mover', unitId: unit.id, path: pathTo(range, bestDefSpot) ?? [] };
        }
        return { type: 'mover', unitId: unit.id, path: [] };
    }
    // Patrulha: se houver waypoints definidos, avança para o próximo waypoint
    if (profile === 'patrulha' && options.patrolWaypoints && options.patrolWaypoints.length > 0) {
        const targetWaypoint = options.patrolWaypoints[0];
        if (targetWaypoint) {
            let bestSpot = unit.position;
            let bestDistance = distance(unit.position, targetWaypoint);
            for (const spot of spots) {
                const d = distance(spot, targetWaypoint);
                if (d < bestDistance || (d === bestDistance && comparePositions(spot, bestSpot) < 0)) {
                    bestDistance = d;
                    bestSpot = spot;
                }
            }
            if (positionKey(bestSpot) !== positionKey(unit.position)) {
                return { type: 'mover', unitId: unit.id, path: pathTo(range, bestSpot) ?? [] };
            }
        }
    }
    // Agressivo e Caçador: avançam na direção do inimigo
    if (enemies.length > 0) {
        // Caçador prefere aproximar-se do comandante ou alvo com menor PV
        let target = enemies[0];
        if (profile === 'cacador') {
            const commander = enemies.find((e) => e.isCommander);
            if (commander) {
                target = commander;
            }
            else {
                target = enemies.reduce((weakest, e) => e.currentHp < weakest.currentHp || (e.currentHp === weakest.currentHp && e.id < weakest.id)
                    ? e
                    : weakest);
            }
        }
        else {
            // Agressivo foca o mais próximo
            target = enemies.reduce((closest, e) => {
                const d1 = spanDistance(unit.position, unit.size, e.position, e.size);
                const d2 = spanDistance(unit.position, unit.size, closest.position, closest.size);
                return d1 < d2 || (d1 === d2 && e.id < closest.id) ? e : closest;
            });
        }
        // A aproximação mede o caminho, não a linha reta. Num corredor — ou num
        // mapa que só se atravessa por teleporte — a linha reta manda a unidade
        // encostar no muro mais próximo do alvo e parar ali o resto da partida.
        const field = travelField(state.map, content, target.position, unit.team, unit.size);
        const reach = (spot) => field.get(positionKey(spot)) ??
            spanDistance(spot, unit.size, target.position, target.size) + 10_000;
        /*
         * Aproximar-se é bom; aproximar-se para dentro do alcance de todo o
         * exército adversário, sem poder atacar ninguém neste turno, é o passo
         * suicida clássico — a unidade avança, para, e apanha de cinco na fase
         * seguinte sem ter dado um golpe.
         *
         * Por isso a escolha deixou de ser "a casa mais perto" e passou a ser um
         * placar: cada casa de aproximação vale pontos, e cada ponto de dano que
         * se leva parado ali desconta. Quando as duas coisas empatam, a unidade
         * fica onde consegue esperar — que é o que um jogador faz ao segurar a
         * linha em vez de entregar a primeira peça.
         */
        const APPROACH_VALUE = 4;
        const score = (spot) => -reach(spot) * APPROACH_VALUE - exposure(spot);
        let bestSpot = unit.position;
        let bestScore = score(unit.position);
        for (const spot of spots) {
            const value = score(spot);
            if (value > bestScore || (value === bestScore && comparePositions(spot, bestSpot) < 0)) {
                bestScore = value;
                bestSpot = spot;
            }
        }
        if (positionKey(bestSpot) !== positionKey(unit.position)) {
            return { type: 'mover', unitId: unit.id, path: pathTo(range, bestSpot) ?? [] };
        }
    }
    return { type: 'mover', unitId: unit.id, path: [] };
}
//# sourceMappingURL=profiles.js.map